import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../../components/layout/ProfessionalLayout';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { Loader2, Clock, ArrowLeft } from 'lucide-react';
import './RequestRecordPage.css';

const RequestRecordPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    company: '',
    title: '',
    startDate: '',
    endDate: '',
    currentlyWorking: false,
    hrEmail: ''
  });

  const [submittedRecord, setSubmittedRecord] = useState(null);

  const steps = [
    { label: 'Role details', number: 1 },
    { label: 'Review and confirm', number: 2 },
    { label: 'Sent', number: 3 }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep0 = () => {
    const newErrors = {};
    
    if (!formData.company.trim()) {
      newErrors.company = 'Enter a company name to continue.';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Enter a role or title to continue.';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Select a start date to continue.';
    }
    if (!formData.hrEmail || !formData.hrEmail.trim()) {
      newErrors.hrEmail = 'Enter an employer email to send the verification request.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.hrEmail)) {
      newErrors.hrEmail = 'Enter a valid email address.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (validateStep0()) {
        setCurrentStep(1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        employer_name: formData.company,
        job_title: formData.title,
        start_date: formData.startDate,
        end_date: formData.currentlyWorking ? null : formData.endDate,
        is_current: formData.currentlyWorking,
        employer_email: formData.hrEmail || ''
      };

      const response = await employeeAPI.createRecordRequest(payload);
      setSubmittedRecord(response.data);
      setCurrentStep(2);
      toast.success('Record request sent successfully!');
    } catch (error) {
      console.error('Error submitting record request:', error);
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateRange = () => {
    const start = formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-';
    const end = formData.currentlyWorking ? 'Present' : (formData.endDate ? new Date(formData.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '-');
    return `${start} – ${end}`;
  };

  return (
    <ProfessionalLayout>
      <div className="request-shell">
        {/* Stepper */}
        <div className="request-stepper" data-testid="request-stepper">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`stepper-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            >
              <div className="stepper-line" />
              <div className="stepper-dot">{step.number}</div>
              <div className="stepper-label">{step.label}</div>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="request-card">
          
          {/* Step 0: Role Details Form */}
          {currentStep === 0 && (
            <div className="request-panel" data-testid="step-role-details">
              <div className="request-eyebrow">NEW RECORD REQUEST</div>
              <h1 className="request-title">Tell us about the role.</h1>
              <p className="request-subtitle">
                We will prepare a co-signing request for the employer to complete. 
                You confirm everything before it is sent.
              </p>

              <div className="request-form">
                <div className="form-group">
                  <label className="form-label" htmlFor="company">Company name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className={`form-input ${errors.company ? 'error' : ''}`}
                    placeholder="e.g. Stripe"
                    value={formData.company}
                    onChange={handleChange}
                    maxLength={80}
                    data-testid="input-company"
                  />
                  {errors.company && <div className="field-error">{errors.company}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="title">Role / title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className={`form-input ${errors.title ? 'error' : ''}`}
                    placeholder="e.g. Senior Product Manager"
                    value={formData.title}
                    onChange={handleChange}
                    maxLength={80}
                    data-testid="input-title"
                  />
                  {errors.title && <div className="field-error">{errors.title}</div>}
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label" htmlFor="startDate">Start date</label>
                    <input
                      type="month"
                      id="startDate"
                      name="startDate"
                      className={`form-input ${errors.startDate ? 'error' : ''}`}
                      value={formData.startDate}
                      onChange={handleChange}
                      data-testid="input-start-date"
                    />
                    {errors.startDate && <div className="field-error">{errors.startDate}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="endDate">End date</label>
                    <input
                      type="month"
                      id="endDate"
                      name="endDate"
                      className="form-input"
                      value={formData.endDate}
                      onChange={handleChange}
                      disabled={formData.currentlyWorking}
                      data-testid="input-end-date"
                    />
                  </div>
                </div>

                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    name="currentlyWorking"
                    checked={formData.currentlyWorking}
                    onChange={handleChange}
                    data-testid="checkbox-current"
                  />
                  <span>I currently work here</span>
                </label>

                <div className="form-group">
                  <label className="form-label" htmlFor="hrEmail">Employer HR contact email</label>
                  <input
                    type="email"
                    id="hrEmail"
                    name="hrEmail"
                    className={`form-input ${errors.hrEmail ? 'error' : ''}`}
                    placeholder="hr@company.com"
                    value={formData.hrEmail}
                    onChange={handleChange}
                    maxLength={120}
                    required
                    data-testid="input-hr-email"
                  />
                  {errors.hrEmail && <div className="field-error">{errors.hrEmail}</div>}
                  <div className="form-hint">
                    We will send the verification request to this email address. This must be someone who can verify your employment.
                  </div>
                </div>

                <div className="employer-preview-note">
                  <strong>What your employer will see:</strong> a request naming you and the role above, 
                  with a link to complete a structured assessment (dates, performance rating, and a closing statement). 
                  They cannot see any of your other records or your VETT Score.
                </div>

                <div className="request-nav">
                  <Link to="/professional/dashboard" className="pro-btn pro-btn-ghost">
                    Cancel
                  </Link>
                  <button 
                    className="pro-btn pro-btn-primary" 
                    onClick={handleNext}
                    data-testid="btn-review"
                  >
                    Review request →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Review */}
          {currentStep === 1 && (
            <div className="request-panel" data-testid="step-review">
              <div className="request-eyebrow">REVIEW BEFORE SENDING</div>
              <h1 className="request-title">Double-check the details.</h1>
              <p className="request-subtitle">
                Once sent, the employer will be asked to confirm or amend these fields. 
                You will review their response before anything is published.
              </p>

              <div className="review-details">
                <div className="review-row">
                  <span>Company</span>
                  <span data-testid="review-company">{formData.company}</span>
                </div>
                <div className="review-row">
                  <span>Role</span>
                  <span data-testid="review-title">{formData.title}</span>
                </div>
                <div className="review-row">
                  <span>Dates</span>
                  <span data-testid="review-dates">{formatDateRange()}</span>
                </div>
                <div className="review-row">
                  <span>Sent to</span>
                  <span data-testid="review-hr">{formData.hrEmail || 'General HR (auto-routed)'}</span>
                </div>
              </div>

              <div className="employer-preview-note">
                <strong>Both parties must confirm.</strong> The record stays as "Awaiting employer" until 
                they complete their assessment. Then it comes back to you to acknowledge and co-sign before 
                it locks and publishes to your VETT ID.
              </div>

              <div className="request-nav">
                <button className="pro-btn pro-btn-ghost" onClick={handleBack}>
                  Back
                </button>
                <button 
                  className="pro-btn pro-btn-primary" 
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="btn-send"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send request →'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Sent / Waiting */}
          {currentStep === 2 && (
            <div className="request-panel waiting-panel" data-testid="step-sent">
              <div className="waiting-state">
                <div className="waiting-icon">
                  <Clock className="w-8 h-8" />
                </div>
                <div className="request-eyebrow" style={{ textAlign: 'center' }}>REQUEST SENT</div>
                <h1 className="request-title" style={{ textAlign: 'center' }}>
                  Sent. Now we wait on the employer.
                </h1>
                <p className="request-subtitle" style={{ textAlign: 'center', maxWidth: '480px', margin: '0 auto' }}>
                  We emailed <strong>{formData.company}</strong> about your role as <strong>{formData.title}</strong>. 
                  You will get a notification the moment they respond.
                </p>

                <div className="waiting-meta">
                  <div className="waiting-meta-item">
                    <div className="waiting-meta-label">STATUS</div>
                    <div className="waiting-meta-val">
                      <span className="pro-status-badge pro-status-invited">Awaiting employer</span>
                    </div>
                  </div>
                  <div className="waiting-meta-item">
                    <div className="waiting-meta-label">TYPICAL RESPONSE TIME</div>
                    <div className="waiting-meta-val">3 to 5 business days</div>
                  </div>
                </div>

                <div className="waiting-actions">
                  <Link to="/professional/dashboard" className="pro-btn pro-btn-ghost">
                    <ArrowLeft className="w-4 h-4" />
                    Back to dashboard
                  </Link>
                  {submittedRecord?.id && (
                    <Link 
                      to={`/professional/records/${submittedRecord.id}`} 
                      className="pro-btn pro-btn-primary"
                    >
                      View this request →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ProfessionalLayout>
  );
};

export default RequestRecordPage;
